'use client';
import { useT, useApiLang } from '@/lib/useT';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import toast from 'react-hot-toast';
import { Mail, Lock, User, Phone, Globe, ArrowRight } from 'lucide-react';
import { Button, Input, Select } from '@/components/ui';
import { AuthShell, AuthCard, AuthField, AuthLinks, AuthLink } from '@/components/shell/AuthShell';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

const COUNTRIES = [
  { code: 'DE', label: 'Deutschland', flag: '🇩🇪' },
  { code: 'AT', label: 'Österreich', flag: '🇦🇹' },
  { code: 'CH', label: 'Schweiz', flag: '🇨🇭' },
  { code: 'PL', label: 'Polska', flag: '🇵🇱' },
  { code: 'CZ', label: 'Česko', flag: '🇨🇿' },
  { code: 'UA', label: 'Україна', flag: '🇺🇦' },
];

export default function CustomerRegisterPage() {
  const t = useT();
  const apiLang = useApiLang();
  const [form, setForm] = useState({ name: '', surname: '', email: '', password: '', phone: '', country: 'DE', termsAccepted: false, privacyAccepted: false, marketingAccepted: false });
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { setAuth } = useAuthStore();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error(t('reg_pw_too_short')); return; }
    setLoading(true);
    try {
      const data = await api.post('/auth/register-customer', { ...form, language: apiLang });
      setAuth(data.accessToken, data.refreshToken, data.user);
      toast.success(t('reg_customer_welcome'));
      router.push('/customer/onboarding');
    } catch (err: any) {
      toast.error(err.message || t('reg_failed'));
    } finally { setLoading(false); }
  };

  const label = (key: string, fallback: string) => {
    const v = t(key);
    return !v || v === key ? fallback : v;
  };

  return (
    <AuthShell
      kicker={label('reg_cust_kicker', 'For shippers')}
      headline={label('reg_cust_headline', 'Book a carrier and watch the shipment move.')}
      lede={label('reg_cust_lede', 'Place an order, get a price, follow the truck on the map and download the CMR when it is delivered.')}
      points={[
        label('reg_cust_point_1', 'Verified carriers with ratings'),
        label('reg_cust_point_2', 'Live tracking link you can forward to your client'),
        label('reg_cust_point_3', 'Invoices and delivery documents in your account'),
      ]}
    >
      <AuthCard
        title={label('reg_cust_title', 'Create a customer account')}
        subtitle={label('reg_cust_subtitle', 'Free — you pay per shipment.')}
        footer={
          <span>
            {label('reg_already', 'Already have an account?')}{' '}
            <AuthLink href="/login">{label('reg_signin', 'Sign in')}</AuthLink>
          </span>
        }
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <AuthField label={label('reg_first_name', 'First name')} icon={User}>
              <Input name="name" value={form.name} onChange={handleChange} required placeholder="Max" autoComplete="given-name" />
            </AuthField>
            <AuthField label={label('reg_last_name', 'Last name')}>
              <Input name="surname" value={form.surname} onChange={handleChange} required placeholder="Mustermann" autoComplete="family-name" />
            </AuthField>
          </div>

          <AuthField label={label('login_email', 'Email address')} icon={Mail}>
            <Input type="email" name="email" value={form.email} onChange={handleChange}
                   required autoComplete="email" placeholder="max@beispiel.de" />
          </AuthField>

          <AuthField label={label('reg_phone', 'Phone')} icon={Phone}
                     hint={label('reg_cust_phone_hint', 'The carrier calls this number about pickup')}>
            <Input type="tel" name="phone" value={form.phone} onChange={handleChange}
                   autoComplete="tel" placeholder="+49 30 12345678" />
          </AuthField>

          <AuthField label={label('login_password', 'Password')} icon={Lock}
                     hint={label('reg_min_chars', 'At least 8 characters')}>
            <Input type="password" name="password" value={form.password} onChange={handleChange}
                   required minLength={8} autoComplete="new-password" placeholder="••••••••" />
          </AuthField>

          <AuthField label={label('reg_country', 'Country')} icon={Globe}>
            <Select name="country" value={form.country} onChange={handleChange}>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.label}</option>)}
            </Select>
          </AuthField>

          <div className="flex flex-col gap-2.5 pt-1">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={form.termsAccepted && form.privacyAccepted}
                onChange={(e) => setForm((prev) => ({ ...prev, termsAccepted: e.target.checked, privacyAccepted: e.target.checked }))}
                className="mt-0.5 w-4 h-4 accent-[var(--brand)] shrink-0"
              />
              <span className="text-[12px] leading-relaxed text-[var(--text-secondary)]">
                {t('consent_terms_prefix')}{' '}
                <a href="https://logixa.one/agb" target="_blank" rel="noopener noreferrer" className="text-[var(--brand)] underline">{t('consent_terms_link')}</a>
                {' '}{t('consent_and')}{' '}
                <a href="https://logixa.one/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[var(--brand)] underline">{t('consent_privacy_link')}</a>
                {' '}{t('consent_terms_suffix')}
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={form.marketingAccepted}
                onChange={(e) => setForm((prev) => ({ ...prev, marketingAccepted: e.target.checked }))}
                className="mt-0.5 w-4 h-4 accent-[var(--brand)] shrink-0"
              />
              <span className="text-[12px] leading-relaxed text-[var(--text-muted)]">{t('consent_marketing')}</span>
            </label>
          </div>

          <Button type="submit" variant="primary" disabled={loading || !form.termsAccepted}
                  className="w-full h-11 text-[14px]" icon={loading ? undefined : ArrowRight}>
            {loading ? label('reg_loading', 'Creating account…') : label('reg_cust_submit', 'Create customer account')}
          </Button>
        </form>
      </AuthCard>

      <AuthLinks>
        <span>
          {label('reg_carrier_link', 'Are you a carrier?')}{' '}
          <AuthLink href="/register">{label('reg_company_link_here', 'Company registration')}</AuthLink>
        </span>
      </AuthLinks>
    </AuthShell>
  );
}
